import { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useRolePerms } from '../features/auth/useRolePerms.js';
import { DeploymentDetailModal } from '../components/domain/DeploymentDetailModal.js';
import { ManualLogModal } from '../components/domain/ManualLogModal.js';
import { UploadModal } from '../components/domain/UploadModal.js';
import { DnaRecordModal } from '../components/domain/DnaRecordModal.js';
import { DnaEditModal } from '../components/domain/DnaEditModal.js';
import { WorkerDetail } from '../components/domain/WorkerDetail.js';
import { SearchableSelect } from '../components/primitives/SearchableSelect.js';
import { WaIcon } from '../components/primitives/WaIcon.js';
import { toast } from '../lib/toast.js';
import { useResetSub } from '../features/filters/useFilters.js';
import { waLink, openWaStaggered } from '../lib/wa.js';
import { avEl, ratingStars, coreBadge, rtwBadge, availBadge, type BadgeWorker, type BadgeAvailabilitySignal } from '../lib/workerBadges.js';
import { buildDeploymentWeekModel, localDateStr, weekStartFor, WEEK_DAY_LABELS } from '@cls/shared';

interface Deployment {
  id: string;
  code: string | null;
  workerId: string;
  workerName: string;
  workerRole: string | null;
  workerPhone: string | null;
  projectId: string;
  projectCode: string | null;
  projectName: string | null;
  projectAddress: string | null;
  projectPostcode: string | null;
  startDate: string;
  startTime: string | null;
  duration: string;
  endDate: string | null;
  supervisorId: string | null;
  supervisorName: string | null;
  supervisorPhone: string | null;
  supervisorEmail: string | null;
  meet: string | null;
  notes: string | null;
  waMessageSent: boolean;
  requesterId: string | null;
  requesterName: string | null;
  requesterEmail: string | null;
  requesterPhone: string | null;
  requesterRole: string | null;
  requesterCustomer: string | null;
  dispatchedBy: string | null;
  dispatchedAt: string | null;
  status: 'active' | 'completed' | 'cancelled' | 'sick' | 'holiday';
  arrivalStatus: 'pending' | 'confirmed' | 'dna' | null;
  dnaRecordedBy: string | null;
  dnaRecordedAt: string | null;
  dnaNotes: string | null;
}

interface DnaRecord {
  id: string;
  code: string | null;
  deployId: string | null;
  workerId: string;
  workerName: string;
  workerRole: string | null;
  workerPhone: string | null;
  projectId: string;
  projectCode: string | null;
  projectName: string | null;
  scheduledDate: string;
  scheduledTime: string | null;
  recordedAt: string;
  recordedBy: string;
  notes: string | null;
}

interface WorkerLite extends BadgeWorker {
  id: string;
  name: string;
  phone?: string;
  emergency?: string | null;
  photo?: string | null;
  role?: string;
  agencyCode?: string | null;
  companyRef?: string | null;
}

interface Absence {
  id: string;
  workerId: string;
  type: 'sick' | 'holiday';
  startDate: string;
  endDate: string;
}

type Tab = 'records' | 'week' | 'arrivals' | 'dna';
type SortDir = 1 | -1;

function fmtDate(iso: string | null | undefined, opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' }): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', opts);
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function yesterdayISO(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

const STATUS_BADGE: Record<string, string> = { active: 'b-active', completed: 'b-dep-done', cancelled: 'b-cancelled', sick: 'b-sick', holiday: 'b-holiday' };

export function DeployLog() {
  const { canImport, canExport, canDeploy, isSiteUser } = useRolePerms();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>('records');
  const [search, setSearch] = useState('');
  const [fWorker, setFWorker] = useState('');
  const [fProject, setFProject] = useState(searchParams.get('projectId') ?? '');
  const [fStatus, setFStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortCol, setSortCol] = useState<keyof Deployment | 'id'>('startDate');
  const [sortDir, setSortDir] = useState<SortDir>(-1);
  const [weekStart, setWeekStart] = useState(() => weekStartFor(new Date()));
  const [weekWorkerId, setWeekWorkerId] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [workerDetailId, setWorkerDetailId] = useState<string | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [dnaTarget, setDnaTarget] = useState<{ deploymentId: string; workerName: string; projectName: string } | null>(null);
  const [dnaEdit, setDnaEdit] = useState<{ id: string; code: string | null; workerName: string; workerId: string; projectId: string; scheduledDate: string; scheduledTime: string | null; recordedBy: string; notes: string | null } | null>(null);
  const qc = useQueryClient();

  useResetSub(() => {
    setSearch(''); setFWorker(''); setFProject(''); setFStatus(''); setDateFrom(''); setDateTo('');
  });

  const { data: deployments = [] } = useQuery({
    queryKey: ['deployments'],
    queryFn: () => api.get<Deployment[]>('/deployments').then((r) => r.data),
  });
  const { data: yesterdays = [] } = useQuery({
    queryKey: ['deployments', 'yesterday'],
    queryFn: () => api.get<Deployment[]>('/deployments/yesterday').then((r) => r.data),
    enabled: !isSiteUser,
  });
  const { data: dna = [] } = useQuery({
    queryKey: ['dna-records'],
    queryFn: () => api.get<DnaRecord[]>('/dna-records').then((r) => r.data),
  });
  const { data: workers = [] } = useQuery({
    queryKey: ['workers'],
    queryFn: () => api.get<WorkerLite[]>('/workers').then((r) => r.data),
  });
  const { data: signals = [] } = useQuery<BadgeAvailabilitySignal[]>({
    queryKey: ['availability-signals'],
    queryFn: () => api.get<BadgeAvailabilitySignal[]>('/availability-signals').then((r) => r.data),
  });
  const { data: absences = [] } = useQuery({
    queryKey: ['absences'],
    queryFn: () => api.get<Absence[]>('/absences').then((r) => r.data).catch(() => [] as Absence[]),
  });

  const weekModel = useMemo(() => buildDeploymentWeekModel(
    {
      workers: workers.map((w) => ({ id: w.id, name: w.name, role: w.role ?? '', agencyCode: w.agencyCode, companyRef: w.companyRef })),
      deployments,
      absences,
    },
    { weekStart, filters: { search, projectId: fProject, workerId: weekWorkerId } },
  ), [workers, deployments, absences, weekStart, search, fProject, weekWorkerId]);

  function shiftWeek(days: number) {
    const d = new Date(`${weekStart}T00:00:00`);
    d.setDate(d.getDate() + days);
    setWeekStart(localDateStr(d));
  }

  function weekExportQuery(): string {
    const params = new URLSearchParams({ week: weekStart });
    if (search.trim()) params.set('search', search.trim());
    if (fProject) params.set('projectId', fProject);
    if (weekWorkerId) params.set('workerId', weekWorkerId);
    return params.toString();
  }

  const workersById = useMemo(() => {
    const m = new Map<string, WorkerLite>();
    for (const w of workers) m.set(w.id, w);
    return m;
  }, [workers]);

  const dnaCountByWorker = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of dna) m.set(d.workerId, (m.get(d.workerId) ?? 0) + 1);
    return m;
  }, [dna]);

  const confirmArrival = useMutation({
    mutationFn: async (deploymentId: string) => api.post(`/deployments/${deploymentId}/confirm`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deployments'] });
      qc.invalidateQueries({ queryKey: ['deployments', 'yesterday'] });
      toast.success('Arrival confirmed');
    },
    onError: () => toast.error('Update failed'),
  });

  const markAll = useMutation({
    mutationFn: async () => api.post('/deployments/confirm-all-yesterday', {}),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ['deployments'] });
      qc.invalidateQueries({ queryKey: ['deployments', 'yesterday'] });
      const n = res?.data?.count ?? 0;
      toast.success('All Confirmed', `${n} pending arrival${n !== 1 ? 's' : ''} marked confirmed`);
    },
    onError: () => toast.error('Update failed'),
  });

  const delDeploy = useMutation({
    mutationFn: async (id: string) => api.delete(`/deployments/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deployments'] });
      qc.invalidateQueries({ queryKey: ['deployments', 'yesterday'] });
      toast('Deleted', 'Record removed', 'warn');
    },
    onError: () => toast.error('Delete failed'),
  });

  const delDna = useMutation({
    mutationFn: async (id: string) => api.delete(`/dna-records/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dna-records'] });
      toast('Deleted', 'DNA record removed', 'warn');
    },
    onError: () => toast.error('Delete failed'),
  });

  const workerOptions = useMemo(() => [...new Set(deployments.map((d) => d.workerName))].sort(), [deployments]);
  const projectOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const d of deployments) {
      if (!byId.has(d.projectId)) byId.set(d.projectId, d.projectName ?? d.projectId);
    }
    return Array.from(byId, ([id, name]) => ({ id, name }));
  }, [deployments]);

  const filtered = useMemo(() => {
    let r = [...deployments];
    const s = search.toLowerCase().trim();
    if (s) r = r.filter((d) => [d.code, d.id, d.workerName, d.workerRole, d.projectId, d.projectName, d.supervisorName, d.notes, d.meet].filter(Boolean).join(' ').toLowerCase().includes(s));
    if (fWorker) r = r.filter((d) => d.workerName === fWorker);
    if (fProject) r = r.filter((d) => d.projectId === fProject);
    if (fStatus) r = r.filter((d) => d.status === fStatus);
    // Temporal overlap with the requested window: a deployment that started
    // before `dateFrom` still counts if it's active during the window.
    // Cancelled records never covered any period — match them by start date only.
    if (dateFrom) r = r.filter((d) => d.status === 'cancelled'
      ? d.startDate.slice(0, 10) >= dateFrom
      : (!d.endDate || d.endDate.slice(0, 10) >= dateFrom));
    if (dateTo) r = r.filter((d) => d.startDate.slice(0, 10) <= dateTo);
    return r;
  }, [deployments, search, fWorker, fProject, fStatus, dateFrom, dateTo]);

  const rows = useMemo(() => {
    if (!sortCol) return filtered;
    const sorted = [...filtered].sort((a, b) => {
      const av = (a as any)[sortCol] ?? '';
      const bv = (b as any)[sortCol] ?? '';
      if (av < bv) return -1 * sortDir;
      if (av > bv) return 1 * sortDir;
      return 0;
    });
    return sorted;
  }, [filtered, sortCol, sortDir]);

  function colSort(col: keyof Deployment | 'id') {
    if (sortCol === col) setSortDir((d) => (d * -1) as SortDir);
    else { setSortCol(col); setSortDir(1); }
  }

  function sortArrow(col: keyof Deployment | 'id'): string {
    if (sortCol !== col) return '⇅';
    return sortDir === 1 ? '↑' : '↓';
  }

  function thSort(label: string, col: keyof Deployment | 'id') {
    const active = sortCol === col;
    return (
      <th onClick={() => colSort(col)} className={active ? 'sorted' : ''} style={{ cursor: 'pointer' }}>
        {label} <span className="sort-arrow" style={{ opacity: active ? 1 : 0.4 }}>{sortArrow(col)}</span>
      </th>
    );
  }

  const totalRecords = deployments.length;
  const activeCount = deployments.filter((d) => d.status === 'active').length;
  const waSent = deployments.filter((d) => d.waMessageSent).length;
  const projectsCovered = new Set(deployments.map((d) => d.projectId)).size;

  const dnaBadge = yesterdays.filter((d) => d.arrivalStatus !== 'confirmed' && d.arrivalStatus !== 'dna' && d.status === 'active').length;

  const clearF = () => { setSearch(''); setFWorker(''); setFProject(''); setFStatus(''); setDateFrom(''); setDateTo(''); };

  function buildExportQuery(): string {
    const params = new URLSearchParams();
    if (fStatus) params.set('status', fStatus);
    if (dateFrom) params.set('startDateFrom', dateFrom);
    if (dateTo) params.set('startDateTo', dateTo);
    if (fProject) params.set('projectId', fProject);
    if (fWorker) {
      const w = workers.find((x) => x.name === fWorker);
      if (w) params.set('workerId', w.id);
    }
    const s = search.trim();
    if (s) params.set('search', s);
    return params.toString();
  }

  function confirmDeleteDeploy(id: string) {
    if (window.confirm(`Delete ${id}?`)) delDeploy.mutate(id);
  }

  // ── Arrivals tab data ─────────────────────────────────────────────
  const yest = yesterdayISO();
  const ongoing = useMemo(() => {
    return deployments.filter((d) => d.status === 'active' && d.startDate.slice(0, 10) <= yest);
  }, [deployments, yest]);
  const arrConfirmed = ongoing.filter((d) => d.arrivalStatus === 'confirmed').length;
  const arrDna = ongoing.filter((d) => d.arrivalStatus === 'dna').length;
  const arrPending = ongoing.filter((d) => d.arrivalStatus !== 'confirmed' && d.arrivalStatus !== 'dna').length;

  function bulkWaWorkers() {
    const pending = ongoing.filter((d) => d.arrivalStatus !== 'confirmed' && d.arrivalStatus !== 'dna' && d.workerPhone);
    if (!pending.length) { toast('Nothing to do', 'No pending arrivals', 'info'); return; }
    const opened = openWaStaggered(pending.map((r) => ({
      phone: r.workerPhone || '',
      message: `Hi ${r.workerName.split(' ')[0]}, just checking — did you arrive at ${r.projectName} this morning? Please confirm. Thanks, CLS Team.`,
    })), 600);
    toast.success('WhatsApp Opened', `Messages opened for ${opened} worker${opened !== 1 ? 's' : ''}`);
  }

  function bulkWaSupervisors() {
    type SupGroup = { phone: string; supName: string; projects: Record<string, { projectName: string; workers: string[] }> };
    const bySup: Record<string, SupGroup> = {};
    for (const r of ongoing) {
      const phone = r.supervisorPhone || '';
      if (!phone) continue;
      if (!bySup[phone]) bySup[phone] = { phone, supName: r.supervisorName || '', projects: {} };
      const pid = r.projectId;
      if (!bySup[phone].projects[pid]) bySup[phone].projects[pid] = { projectName: r.projectName ?? r.projectId, workers: [] };
      bySup[phone].projects[pid].workers.push(r.workerName);
    }
    const sups = Object.values(bySup);
    if (!sups.length) { toast('No supervisors', 'No supervisor phone numbers found', 'warn'); return; }
    sups.forEach((s, i) => {
      setTimeout(() => {
        const lines = Object.values(s.projects).map((p) => `${p.projectName}: ${p.workers.join(', ')}`).join('\n');
        const msg = `Hi ${s.supName.split(' ')[0]}, please confirm all deployed workers have arrived on site today:\n${lines}\nIf anyone has not arrived, please reply with their name. Thanks, CLS Team.`;
        window.open(waLink(s.phone, msg), '_blank');
      }, i * 700);
    });
    toast.success('Supervisor Messages Opened', `${sups.length} supervisor${sups.length !== 1 ? 's' : ''} messaged`);
  }

  function bulkWaRequesters() {
    type ReqGroup = { phone: string; name: string; workers: string[] };
    const byReq: Record<string, ReqGroup> = {};
    for (const r of ongoing) {
      if (!r.requesterPhone) continue;
      if (!byReq[r.requesterPhone]) byReq[r.requesterPhone] = { phone: r.requesterPhone, name: r.requesterName ?? r.requesterPhone, workers: [] };
      byReq[r.requesterPhone].workers.push(`${r.workerName} (${r.projectName ?? r.projectId})`);
    }
    const reqs = Object.values(byReq);
    if (!reqs.length) { toast('No requesters', 'No deployed records have requester phone numbers', 'warn'); return; }
    reqs.forEach((req, i) => {
      setTimeout(() => {
        const msg = `Hi ${req.name.split(' ')[0]}, just confirming the following workers deployed yesterday: ${req.workers.join(', ')}. Please let us know if anyone did not arrive. Thanks, CLS Team.`;
        window.open(waLink(req.phone, msg), '_blank');
      }, i * 600);
    });
    toast.success('WhatsApp Opened', `${reqs.length} requester message${reqs.length !== 1 ? 's' : ''} opened`);
  }

  // ── DNA tab data ─────────────────────────────────────────────────
  const dnaByWorker = useMemo(() => {
    const m = new Map<string, { workerId: string; workerName: string; workerRole: string | null; count: number; latest: string }>();
    for (const d of dna) {
      const existing = m.get(d.workerId);
      if (!existing) {
        m.set(d.workerId, { workerId: d.workerId, workerName: d.workerName, workerRole: d.workerRole, count: 1, latest: d.scheduledDate });
      } else {
        existing.count++;
        if (d.scheduledDate > existing.latest) existing.latest = d.scheduledDate;
      }
    }
    return [...m.values()].sort((a, b) => b.count - a.count);
  }, [dna]);

  const dnaSorted = useMemo(() => [...dna].sort((a, b) => (b.recordedAt > a.recordedAt ? 1 : -1)), [dna]);

  function dnaSeverityColor(count: number): string {
    if (count >= 3) return '#ef4444';
    if (count >= 2) return '#fbbf24';
    return 'var(--muted)';
  }

  function confirmDeleteDna(id: string) {
    if (window.confirm('Delete this DNA record?')) delDna.mutate(id);
  }

  // ── Render ──────────────────────────────────────────────────────
  return (
    <div className="page" id="page-deploylog">
      <div className="hbar">
        <div><div className="ph">Deployment Records</div></div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {canImport ? <button className="btn btn-ghost btn-sm" onClick={() => setImportOpen(true)}>⬆ Import</button> : null}
          {canExport ? <button className="btn btn-ghost btn-sm" onClick={() => { const qs = buildExportQuery(); window.open(`/api/reports/deployment-records.xlsx${qs ? `?${qs}` : ''}`, '_blank'); toast.info('Export started', 'Deployment records · XLSX'); }}>⬇ CSV</button> : null}
          {canExport ? <button className="btn btn-ghost btn-sm" onClick={() => { const qs = buildExportQuery(); window.open(`/api/reports/deployment-records.pdf${qs ? `?${qs}` : ''}`, '_blank'); toast.info('Export started', 'Deployment records · PDF'); }}>⬇ PDF</button> : null}
          {canDeploy ? <button className="btn btn-primary btn-sm" onClick={() => setManualOpen(true)}>+ Manual Entry</button> : null}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 2, marginBottom: 14, borderBottom: '2px solid var(--border)', flexWrap: 'wrap' }}>
        <button className={`ts-tab ${tab === 'records' ? 'ts-tab-active' : ''}`} id="dl-tab-records" onClick={() => setTab('records')}>📋 All Records</button>
        <button className={`ts-tab ${tab === 'week' ? 'ts-tab-active' : ''}`} id="dl-tab-week" onClick={() => setTab('week')}>📅 Week Grid</button>
        <button className={`ts-tab ${tab === 'arrivals' ? 'ts-tab-active' : ''}`} id="dl-tab-arrivals" onClick={() => setTab('arrivals')} style={{ position: 'relative' }}>
          ✅ Yesterday's Arrivals
          {dnaBadge > 0 ? (
            <span id="dl-dna-badge" style={{ position: 'absolute', top: 4, right: 4, background: '#ef4444', color: '#fff', borderRadius: '50%', width: 14, height: 14, fontSize: 9, fontWeight: 700, lineHeight: '14px', textAlign: 'center' }}>{dnaBadge}</span>
          ) : null}
        </button>
        <button className={`ts-tab ${tab === 'dna' ? 'ts-tab-active' : ''}`} id="dl-tab-dna" onClick={() => setTab('dna')}>⚠ DNA Records</button>
      </div>

      {/* ── RECORDS TAB ── */}
      <div id="dl-tab-content-records" style={{ display: tab === 'records' ? 'block' : 'none' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }} id="dl-stats">
          <div className="dl-stat"><div className="dl-stat-val" style={{ color: 'var(--accent)' }}>{totalRecords}</div><div className="dl-stat-lbl">Total Records</div></div>
          <div className="dl-stat"><div className="dl-stat-val" style={{ color: 'var(--live)' }}>{activeCount}</div><div className="dl-stat-lbl">Currently Active</div></div>
          <div className="dl-stat"><div className="dl-stat-val" style={{ color: 'var(--wa)' }}>{waSent}</div><div className="dl-stat-lbl">WhatsApp Sent</div></div>
          <div className="dl-stat"><div className="dl-stat-val" style={{ color: 'var(--info)' }}>{projectsCovered}</div><div className="dl-stat-lbl">Projects Covered</div></div>
        </div>
        <div className="dl-bar">
          <div className="dl-si">
            <span style={{ color: 'var(--faint)', fontSize: 13 }}>⌕</span>
            <input id="dl-search" placeholder="Search records…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="dl-f"><span>Worker:</span>
            <select id="dlf-worker" value={fWorker} onChange={(e) => setFWorker(e.target.value)}>
              <option value="">All</option>
              {workerOptions.map((w) => <option key={w} value={w}>{w}</option>)}
            </select>
          </div>
          <div className="dl-f"><span>Project:</span>
            <select id="dlf-project" value={fProject} onChange={(e) => setFProject(e.target.value)}>
              <option value="">All</option>
              {projectOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="dl-f"><span>Status:</span>
            <select id="dlf-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="sick">Sick</option>
              <option value="holiday">Holiday</option>
            </select>
          </div>
          <div className="dl-f"><span>From:</span><input type="date" id="dlf-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></div>
          <div className="dl-f"><span>To:</span><input type="date" id="dlf-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></div>
          <button className="btn btn-ghost btn-sm" onClick={clearF}>✕</button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }} id="dl-count">Showing {rows.length} of {totalRecords} records</div>
        <div className="card" style={{ overflow: 'auto', maxHeight: 'calc(100vh - 380px)' }}>
          <table>
            <thead id="dl-thead">
              <tr>
                {thSort('Ref', 'id')}
                {thSort('Worker', 'workerName')}
                {thSort('Role', 'workerRole')}
                {thSort('Project', 'projectId')}
                <th>Location</th>
                {thSort('Start', 'startDate')}
                <th>Duration</th>
                <th>Requester</th>
                <th>WA</th>
                {thSort('Status', 'status')}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody id="dl-tbody">
              {rows.length === 0 ? (
                <tr><td colSpan={11} style={{ textAlign: 'center', padding: 30, color: 'var(--muted)' }}>No records match filters</td></tr>
              ) : rows.map((r) => {
                const _w = workersById.get(r.workerId);
                const ref = r.code ?? r.id.slice(0, 8);
                return (
                  <tr key={r.id} onClick={() => setDetailId(r.id)} style={{ cursor: 'pointer' }}>
                    <td style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 9, color: 'var(--faint)' }}>{ref}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {avEl(_w ?? { name: r.workerName }, 22, 8)}
                        <div>
                          <div style={{ fontWeight: 500, fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                            <span>{r.workerName}</span>
                            {coreBadge(_w, true)}
                            {ratingStars(_w, 9)}
                            {rtwBadge(_w)}
                            {availBadge(_w, signals)}
                          </div>
                          {r.workerPhone ? <div style={{ fontSize: 9, color: 'var(--muted)' }}>{r.workerPhone}</div> : null}
                        </div>
                      </div>
                    </td>
                    <td>{r.workerRole ? <span className="badge b-role">{r.workerRole}</span> : '—'}</td>
                    <td style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 10, color: 'var(--accent)' }}>{r.projectCode ?? r.projectId.slice(0, 8)}</td>
                    <td>
                      {r.projectPostcode ? (
                        <a href={`https://www.google.com/maps/search/${encodeURIComponent(r.projectPostcode)}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: 'var(--accent)', fontSize: 10, fontFamily: 'Syne, sans-serif', fontWeight: 700, textDecoration: 'none' }}>📍{r.projectPostcode}</a>
                      ) : <span style={{ color: 'var(--faint)', fontSize: 10 }}>—</span>}
                    </td>
                    <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{r.startDate.slice(0, 10)}</td>
                    <td style={{ fontSize: 11 }}>{r.duration}</td>
                    <td style={{ fontSize: 10, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                      {r.requesterName ? (
                        <>
                          <div style={{ fontWeight: 500 }}>{r.requesterName}</div>
                          {r.requesterCustomer ? <div style={{ fontSize: 9 }}>{r.requesterCustomer}</div> : null}
                        </>
                      ) : '—'}
                    </td>
                    <td style={{ textAlign: 'center' }}>{r.waMessageSent ? <span style={{ color: 'var(--wa)' }}>✓</span> : <span style={{ color: 'var(--faint)' }}>—</span>}</td>
                    <td><span className={`badge ${STATUS_BADGE[r.status] ?? ''}`}>{r.status}</span></td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 3 }}>
                        <button className="btn btn-ghost btn-xs" onClick={() => setDetailId(r.id)}>View</button>
                        {canDeploy ? <button className="btn btn-ghost btn-xs" style={{ color: 'var(--danger)' }} onClick={() => confirmDeleteDeploy(r.id)} disabled={delDeploy.isPending}>✕</button> : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── WEEK GRID TAB ── */}
      <div id="dl-tab-content-week" style={{ display: tab === 'week' ? 'block' : 'none' }}>
        <div className="dl-bar">
          <button className="btn btn-ghost btn-sm" onClick={() => shiftWeek(-7)}>‹ Prev Week</button>
          <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 12, minWidth: 190, textAlign: 'center' }}>
            {weekModel.dates[0]!.slice(8, 10)}/{weekModel.dates[0]!.slice(5, 7)} – {weekModel.dates[6]!.slice(8, 10)}/{weekModel.dates[6]!.slice(5, 7)} · WE {weekModel.weekEnding}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => shiftWeek(7)}>Next Week ›</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setWeekStart(weekStartFor(new Date()))}>This Week</button>
          <div className="dl-si">
            <span style={{ color: 'var(--faint)', fontSize: 13 }}>⌕</span>
            <input id="dlw-search" placeholder="Search name, trade, agency, ref…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="dl-f"><span>Worker:</span>
            <select id="dlw-worker" value={weekWorkerId} onChange={(e) => setWeekWorkerId(e.target.value)}>
              <option value="">All</option>
              {[...workers].sort((a, b) => a.name.localeCompare(b.name)).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </div>
          <div className="dl-f"><span>Project:</span>
            <select id="dlw-project" value={fProject} onChange={(e) => setFProject(e.target.value)}>
              <option value="">All</option>
              {projectOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setFProject(''); setWeekWorkerId(''); }}>✕</button>
          {canExport ? <button className="btn btn-ghost btn-sm" onClick={() => { window.open(`/api/reports/deployment-week.xlsx?${weekExportQuery()}`, '_blank'); toast.info('Export started', 'Weekly deployment · XLSX'); }}>⬇ XLSX</button> : null}
          {canExport ? <button className="btn btn-ghost btn-sm" onClick={() => { window.open(`/api/reports/deployment-week.pdf?${weekExportQuery()}`, '_blank'); toast.info('Export started', 'Weekly deployment · PDF'); }}>⬇ PDF</button> : null}
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }} id="dlw-count">
          {weekModel.totalRows} worker{weekModel.totalRows !== 1 ? 's' : ''} deployed this week · cells show CLS project codes · <span style={{ color: '#f59e0b' }}>SICK</span> / <span style={{ color: '#60a5fa' }}>HOL</span> = recorded absence
        </div>
        <div className="card" style={{ overflow: 'auto', maxHeight: 'calc(100vh - 340px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--surface)' }}>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 10px', minWidth: 160 }}>Worker</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', minWidth: 110 }}>Trade</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Agency</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Ref</th>
                {weekModel.dates.map((ds, i) => (
                  <th key={ds} style={{ textAlign: 'center', padding: '6px 4px', minWidth: 76, opacity: i >= 5 ? .65 : 1 }}>
                    <div style={{ fontSize: 9, color: 'var(--muted)' }}>{WEEK_DAY_LABELS[i]}</div>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700 }}>{ds.slice(8, 10)}/{ds.slice(5, 7)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody id="dlw-tbody">
              {weekModel.totalRows === 0 ? (
                <tr><td colSpan={11} style={{ textAlign: 'center', padding: 30, color: 'var(--muted)' }}>No deployments in this week</td></tr>
              ) : weekModel.groups.flatMap((g) => [
                <tr key={`grp-${g.agency}`}>
                  <td colSpan={11} style={{ background: 'rgba(232,164,39,.1)', padding: '5px 10px', fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 10, color: 'var(--accent)', borderBottom: '1px solid var(--border)' }}>
                    {g.agency} — {g.rows.length} worker{g.rows.length !== 1 ? 's' : ''}
                  </td>
                </tr>,
                ...g.rows.map((r) => (
                  <tr key={r.workerId} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '5px 10px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => setWorkerDetailId(r.workerId)} title="Open profile">{r.name}</td>
                    <td style={{ padding: '5px 8px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>{r.trade}</td>
                    <td style={{ padding: '5px 8px', color: 'var(--info)', whiteSpace: 'nowrap' }}>{r.agency}</td>
                    <td style={{ padding: '5px 8px', fontFamily: 'DM Mono, monospace', color: 'var(--muted)' }}>{r.companyRef || '—'}</td>
                    {r.cells.map((c, i) => (
                      <td
                        key={i}
                        style={{ textAlign: 'center', padding: '4px 3px', opacity: i >= 5 && !c.codes.length && !c.absence ? .45 : 1, cursor: c.deploymentIds.length ? 'pointer' : 'default' }}
                        onClick={() => { if (c.deploymentIds.length) setDetailId(c.deploymentIds[0]!); }}
                        title={c.absence ? `${c.absence === 'sick' ? 'Sick' : 'Holiday'}${c.codes.length ? ` · ${c.codes.join(' / ')}` : ''}` : c.codes.join(' / ')}
                      >
                        {c.absence ? (
                          <span style={{ background: c.absence === 'sick' ? 'rgba(245,158,11,.18)' : 'rgba(96,165,250,.18)', color: c.absence === 'sick' ? '#f59e0b' : '#60a5fa', borderRadius: 4, padding: '2px 6px', fontSize: 9, fontWeight: 700 }}>
                            {c.absence === 'sick' ? 'SICK' : 'HOL'}
                          </span>
                        ) : c.codes.length ? (
                          <span style={{ color: 'var(--accent)', fontFamily: 'DM Mono, monospace', fontSize: 10, fontWeight: 600 }}>{c.codes.join(' / ')}</span>
                        ) : (
                          <span style={{ color: 'var(--faint)' }}>—</span>
                        )}
                      </td>
                    ))}
                  </tr>
                )),
              ])}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── ARRIVALS TAB ── */}
      <div id="dl-tab-content-arrivals" style={{ display: tab === 'arrivals' ? 'block' : 'none' }}>
        {ongoing.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--muted)' }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>✅</div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>No active deployments for {fmtDate(`${yest}T12:00:00`, { weekday: 'long', day: '2-digit', month: 'long' })}</div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 9, marginBottom: 14, flexWrap: 'wrap' }}>
              <div style={{ background: 'var(--live-dim)', border: '1px solid rgba(34,197,94,.25)', borderRadius: 10, padding: '10px 16px', textAlign: 'center', flex: 1, minWidth: 80 }}>
                <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, color: 'var(--live)' }}>{arrConfirmed}</div>
                <div style={{ fontSize: 9, color: 'var(--live)', textTransform: 'uppercase', letterSpacing: 1 }}>Confirmed</div>
              </div>
              <div style={{ background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.25)', borderRadius: 10, padding: '10px 16px', textAlign: 'center', flex: 1, minWidth: 80 }}>
                <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, color: '#ef4444' }}>{arrDna}</div>
                <div style={{ fontSize: 9, color: '#ef4444', textTransform: 'uppercase', letterSpacing: 1 }}>DNA</div>
              </div>
              <div style={{ background: 'var(--accent-dim)', border: '1px solid rgba(232,164,39,.25)', borderRadius: 10, padding: '10px 16px', textAlign: 'center', flex: 1, minWidth: 80 }}>
                <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, color: 'var(--accent)' }}>{arrPending}</div>
                <div style={{ fontSize: 9, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 1 }}>Pending</div>
              </div>
              <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 16px', textAlign: 'center', flex: 1, minWidth: 80 }}>
                <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, color: 'var(--text)' }}>{ongoing.length}</div>
                <div style={{ fontSize: 9, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1 }}>Total</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
              <button className="btn btn-ghost btn-sm" style={{ color: '#25D366', borderColor: '#25D366' }} onClick={bulkWaWorkers}>💬 WhatsApp All — Confirm Arrival</button>
              <button className="btn btn-ghost btn-sm" style={{ color: '#25D366', borderColor: '#25D366' }} onClick={bulkWaSupervisors}>💬 WhatsApp Supervisors — Roll Call</button>
              {canDeploy ? <button className="btn btn-ghost btn-sm" onClick={() => markAll.mutate()} disabled={markAll.isPending}>✓ Mark All Confirmed</button> : null}
              <button className="btn btn-ghost btn-sm" style={{ color: '#25D366', borderColor: '#25D366' }} onClick={bulkWaRequesters}>💬 WhatsApp All Requesters</button>
              <span style={{ fontSize: 11, color: 'var(--muted)', alignSelf: 'center' }}>Date: {fmtDate(`${yest}T12:00:00`, { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</span>
            </div>
            <div className="card" style={{ overflowX: 'auto', padding: 0 }}>
              <table style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Worker</th>
                    <th>Role</th>
                    <th>Project</th>
                    <th>Start Time</th>
                    <th>Supervisor</th>
                    <th>Requester</th>
                    <th>Arrival Status</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {[...ongoing].sort((a, b) => a.workerName.localeCompare(b.workerName)).map((r) => {
                    const _w = workersById.get(r.workerId);
                    const dnaCount = dnaCountByWorker.get(r.workerId) ?? 0;
                    const status = r.arrivalStatus ?? 'pending';
                    const sc = status === 'confirmed' ? { label: 'Confirmed', bg: 'rgba(34,197,94,.12)', color: '#22c55e' }
                      : status === 'dna' ? { label: 'DNA', bg: 'rgba(239,68,68,.12)', color: '#ef4444' }
                      : { label: 'Pending', bg: 'rgba(251,191,36,.12)', color: '#fbbf24' };
                    return (
                      <tr key={r.id}>
                        <td style={{ whiteSpace: 'nowrap', fontWeight: 500, cursor: 'pointer' }} onClick={() => setWorkerDetailId(r.workerId)}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                            <span>{r.workerName}</span>
                            {coreBadge(_w, true)}
                            {ratingStars(_w, 10)}
                            {rtwBadge(_w)}
                            {availBadge(_w, signals)}
                            {dnaCount > 0 ? <span style={{ background: 'rgba(239,68,68,.15)', color: '#ef4444', fontSize: 9, padding: '1px 5px', borderRadius: 4, marginLeft: 4 }} title={`${dnaCount} DNA on record`}>⚠ {dnaCount} DNA</span> : null}
                          </div>
                          {r.workerPhone ? <div style={{ fontSize: 9, color: 'var(--muted)' }}>{r.workerPhone}</div> : null}
                        </td>
                        <td style={{ fontSize: 11, color: 'var(--muted)' }}>{r.workerRole ?? '—'}</td>
                        <td>
                          <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, color: 'var(--accent)', fontSize: 11 }}>{r.projectCode ?? r.projectId.slice(0, 8)}</span>
                          <div style={{ fontSize: 9, color: 'var(--muted)' }}>{r.projectName}</div>
                        </td>
                        <td style={{ fontSize: 11 }}>{r.startTime ?? '—'}</td>
                        <td style={{ fontSize: 11, color: 'var(--muted)' }}>{r.supervisorName ?? '—'}</td>
                        <td style={{ fontSize: 10 }}>
                          {r.requesterName ? (
                            <>
                              <div style={{ fontWeight: 500 }}>{r.requesterName}</div>
                              {r.requesterCustomer ? <div style={{ fontSize: 9, color: 'var(--muted)' }}>{r.requesterCustomer}</div> : null}
                              {r.requesterPhone ? (
                                <div style={{ marginTop: 2 }}>
                                  <a
                                    href={waLink(r.requesterPhone, `Hi ${r.requesterName.split(' ')[0]}, re the deployment of ${r.workerName} at ${r.projectName ?? r.projectId} — please confirm all workers have arrived.`)}
                                    target="_blank" rel="noreferrer"
                                    style={{ color: '#25D366', fontSize: 9 }}
                                  >💬 WhatsApp Requester</a>
                                </div>
                              ) : null}
                            </>
                          ) : <span style={{ color: 'var(--faint)' }}>—</span>}
                        </td>
                        <td>
                          <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 5, fontWeight: 600, background: sc.bg, color: sc.color }}>{sc.label}</span>
                          {status === 'dna' && r.dnaRecordedBy ? (
                            <>
                              <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 3 }}>By {r.dnaRecordedBy} · {fmtDateTime(r.dnaRecordedAt)}</div>
                              {r.dnaNotes ? <div style={{ fontSize: 9, color: 'var(--muted)', fontStyle: 'italic', marginTop: 1 }}>{r.dnaNotes}</div> : null}
                            </>
                          ) : null}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                            {status !== 'confirmed' && canDeploy ? <button className="btn btn-ghost btn-xs" style={{ color: 'var(--live)', borderColor: 'var(--live)' }} onClick={() => confirmArrival.mutate(r.id)} disabled={confirmArrival.isPending}>✓ Arrived</button> : null}
                            {status !== 'dna' && canDeploy ? <button className="btn btn-ghost btn-xs" style={{ color: '#ef4444', borderColor: '#ef4444' }} onClick={() => setDnaTarget({ deploymentId: r.id, workerName: r.workerName, projectName: r.projectName ?? r.projectId })}>✗ DNA</button> : null}
                            {r.workerPhone ? (
                              <a className="btn btn-wa btn-xs" href={waLink(r.workerPhone, `Hi ${r.workerName.split(' ')[0]}, just checking — did you arrive at ${r.projectName} this morning? Please confirm. Thanks, CLS Team.`)} target="_blank" rel="noreferrer" title="Message worker" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><WaIcon size={12} /></a>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 8 }}>
              Shows all active deployments with a start date on or before yesterday. DNA = Did Not Arrive. Click a worker name to open their profile.
            </div>
          </>
        )}
      </div>

      {/* ── DNA TAB ── */}
      <div id="dl-tab-content-dna" style={{ display: tab === 'dna' ? 'block' : 'none' }}>
        {dna.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--muted)' }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>✓</div>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--live)' }}>No DNA records</div>
            <div style={{ fontSize: 11, marginTop: 4 }}>All workers have arrived as expected.</div>
          </div>
        ) : (
          <>
            <div className="sec-title" style={{ marginBottom: 10 }}>Worker DNA Summary</div>
            <div className="card" style={{ overflowX: 'auto', padding: 0, marginBottom: 18 }}>
              <table style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Worker</th>
                    <th>Role</th>
                    <th>DNA Count</th>
                    <th>Last Occurrence</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {dnaByWorker.map((w) => {
                    const _w = workersById.get(w.workerId);
                    return (
                      <tr key={w.workerId} style={{ cursor: 'pointer' }} onClick={() => setWorkerDetailId(w.workerId)}>
                        <td style={{ fontWeight: 500 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                            <span>{w.workerName}</span>
                            {coreBadge(_w, true)}
                            {ratingStars(_w, 10)}
                            {rtwBadge(_w)}
                          </div>
                        </td>
                        <td style={{ fontSize: 11, color: 'var(--muted)' }}>{w.workerRole ?? '—'}</td>
                        <td style={{ textAlign: 'center' }}>
                          <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 14, color: dnaSeverityColor(w.count) }}>{w.count}</span>
                        </td>
                        <td style={{ fontSize: 11 }}>{w.latest.slice(0, 10)}</td>
                        <td><button className="btn btn-ghost btn-xs" onClick={(e) => { e.stopPropagation(); setWorkerDetailId(w.workerId); }}>View Profile</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="sec-title" style={{ marginBottom: 10 }}>Full DNA Log</div>
            <div className="card" style={{ overflowX: 'auto', padding: 0 }}>
              <table style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>DNA Ref</th>
                    <th>Worker</th>
                    <th>Role</th>
                    <th>Project</th>
                    <th>Scheduled</th>
                    <th>Requester</th>
                    <th>Recorded At</th>
                    <th>Recorded By</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dnaSorted.map((d) => {
                    const _w = workersById.get(d.workerId);
                    const parent = d.deployId ? deployments.find((x) => x.id === d.deployId) : null;
                    const ref = d.code ?? d.id.slice(0, 8);
                    return (
                      <tr key={d.id} style={{ cursor: 'pointer' }} onClick={() => setWorkerDetailId(d.workerId)}>
                        <td style={{ fontFamily: 'Syne, sans-serif', fontSize: 9, color: 'var(--faint)', fontWeight: 700 }}>{ref}</td>
                        <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }} onClick={(e) => { e.stopPropagation(); setWorkerDetailId(d.workerId); }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                            <span>{d.workerName}</span>
                            {coreBadge(_w, true)}
                            {ratingStars(_w, 10)}
                            {rtwBadge(_w)}
                          </div>
                        </td>
                        <td style={{ fontSize: 11, color: 'var(--muted)' }}>{d.workerRole ?? '—'}</td>
                        <td>
                          <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, color: 'var(--accent)', fontSize: 11 }}>{d.projectCode ?? d.projectId.slice(0, 8)}</span>
                          <div style={{ fontSize: 9, color: 'var(--muted)' }}>{d.projectName}</div>
                        </td>
                        <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{d.scheduledDate.slice(0, 10)} {d.scheduledTime ?? ''}</td>
                        <td style={{ fontSize: 10 }}>
                          {parent?.requesterName ? (
                            <>
                              <div style={{ fontWeight: 500 }}>{parent.requesterName}</div>
                              {parent.requesterCustomer ? <div style={{ fontSize: 9, color: 'var(--muted)' }}>{parent.requesterCustomer}</div> : null}
                            </>
                          ) : <span style={{ color: 'var(--faint)' }}>—</span>}
                        </td>
                        <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtDateTime(d.recordedAt)}</td>
                        <td style={{ fontSize: 11 }}>{d.recordedBy}</td>
                        <td style={{ fontSize: 11, color: 'var(--muted)', maxWidth: 160 }}>{d.notes ?? '—'}</td>
                        <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                          {canDeploy ? <button className="btn btn-ghost btn-xs" title="Edit" onClick={() => setDnaEdit({ id: d.id, code: d.code, workerName: d.workerName, workerId: d.workerId, projectId: d.projectId, scheduledDate: d.scheduledDate.slice(0, 10), scheduledTime: d.scheduledTime, recordedBy: d.recordedBy, notes: d.notes })}>✏</button> : null}
                          {canDeploy ? <button className="btn btn-danger btn-xs" title="Delete" onClick={() => confirmDeleteDna(d.id)} disabled={delDna.isPending}>✕</button> : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {workerDetailId ? <WorkerDetail workerId={workerDetailId} onClose={() => setWorkerDetailId(null)} /> : null}
      {detailId ? <DeploymentDetailModal deploymentId={detailId} onClose={() => setDetailId(null)} /> : null}
      {manualOpen ? <ManualLogModal onClose={() => setManualOpen(false)} /> : null}
      {importOpen ? <UploadModal entity="deploylog" onClose={() => setImportOpen(false)} /> : null}
      {dnaTarget ? <DnaRecordModal onClose={() => setDnaTarget(null)} deploymentId={dnaTarget.deploymentId} workerName={dnaTarget.workerName} projectName={dnaTarget.projectName} /> : null}
      {dnaEdit ? <DnaEditModal onClose={() => setDnaEdit(null)} dnaId={dnaEdit.id} dnaCode={dnaEdit.code} workerId={dnaEdit.workerId} workerName={dnaEdit.workerName} initial={{ projectId: dnaEdit.projectId, scheduledDate: dnaEdit.scheduledDate, scheduledTime: dnaEdit.scheduledTime, recordedBy: dnaEdit.recordedBy, notes: dnaEdit.notes }} /> : null}
    </div>
  );
}
